import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError } from '~/lib/api';

/**
 * Sign-up kicks off the self-service onboarding sequence:
 *   POST /auth/signup   → user + tenant row, verify-email sent
 *   verify from email   → session issued, land on dashboard
 *
 * We don't log the user in on signup; the backend only mints a session
 * after /auth/verify-email. So this page's "success" state tells them to
 * check their email and sits.
 */
export function SignupPage(): JSX.Element {
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [password, setPassword] = useState('');
  const [terms, setTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (!terms) {
      setError('You need to accept the terms to continue.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await api('/auth/signup', {
        method: 'POST',
        body: {
          firstName,
          lastName,
          email,
          phoneNumber,
          businessName,
          password,
          termsAccepted: true,
        },
      });
      setDone(true);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : 'Something went wrong. Try again.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <main
        data-ground="landing"
        className="grid min-h-full place-items-center bg-surface-canvas p-6"
      >
        <div className="w-full max-w-[400px]">
          <div className="mb-10 flex items-center gap-2.5">
            <Brand />
          </div>
          <h1 className="mb-3 font-serif text-[36px] font-normal leading-[40px] -tracking-[0.02em]">
            Check your <em className="italic text-accent-wax">email</em>
          </h1>
          <p className="mb-8 text-[15px] leading-[22px] text-ink-secondary">
            We sent a verification link to <strong>{email}</strong>. Click the link
            to activate your account, then sign in.
          </p>
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="inline-flex h-9 items-center justify-center gap-2 rounded-md bg-accent-wax px-3.5 text-[14.5px] font-semibold text-accent-wax-ink shadow-[inset_0_1px_0_rgba(255,255,255,.14),0_1px_2px_rgba(94,26,40,.2)] transition-colors hover:bg-accent-wax-deep"
          >
            Back to sign in
          </button>
        </div>
      </main>
    );
  }

  return (
    <main
      data-ground="landing"
      className="grid min-h-full place-items-center bg-surface-canvas p-6"
    >
      <div className="w-full max-w-[440px]">
        <div className="mb-10 flex items-center gap-2.5">
          <Brand />
        </div>

        <h1 className="mb-2 font-serif text-[36px] font-normal leading-[40px] -tracking-[0.02em]">
          Create an <em className="italic text-accent-wax">account</em>
        </h1>
        <p className="mb-8 text-[15px] leading-[22px] text-ink-secondary">
          A Seven account issues cards, holds balance, and runs authorizations.
          You can finish KYB after signing in.
        </p>

        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name">
              <Input value={firstName} onChange={setFirstName} autoComplete="given-name" required />
            </Field>
            <Field label="Last name">
              <Input value={lastName} onChange={setLastName} autoComplete="family-name" required />
            </Field>
          </div>
          <Field label="Work email">
            <Input type="email" value={email} onChange={setEmail} autoComplete="email" required />
          </Field>
          <Field label="Phone">
            <Input
              type="tel"
              value={phoneNumber}
              onChange={setPhoneNumber}
              placeholder="+2348012345678"
              autoComplete="tel"
              required
            />
          </Field>
          <Field label="Business name">
            <Input value={businessName} onChange={setBusinessName} autoComplete="organization" required />
          </Field>
          <Field label="Password">
            <Input type="password" value={password} onChange={setPassword} autoComplete="new-password" required />
          </Field>

          <label className="mt-1 inline-flex items-start gap-2.5 text-[13.5px] text-ink-secondary">
            <input
              type="checkbox"
              checked={terms}
              onChange={(e) => setTerms(e.target.checked)}
              className="mt-[2px] h-3.5 w-3.5 accent-accent-wax"
            />
            <span>
              I agree to Seven's terms of service and privacy policy.
            </span>
          </label>

          {error && (
            <div className="rounded border border-stop/20 bg-stop-tint px-3 py-2 text-[13.5px] text-stop">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="mt-1 inline-flex h-9 items-center justify-center gap-2 rounded-md bg-accent-wax px-3.5 text-[14.5px] font-semibold text-accent-wax-ink shadow-[inset_0_1px_0_rgba(255,255,255,.14),0_1px_2px_rgba(94,26,40,.2)] transition-colors hover:bg-accent-wax-deep disabled:opacity-60"
          >
            {submitting ? 'Creating…' : 'Create account'}
          </button>

          <div className="pt-2 text-center text-[14px] text-ink-secondary">
            Already have an account?{' '}
            <Link to="/login" className="font-semibold text-accent-wax hover:text-accent-wax-deep">
              Sign in
            </Link>
          </div>
        </form>
      </div>
    </main>
  );
}

function Brand(): JSX.Element {
  return (
    <>
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-accent-wax text-[14px] font-semibold text-accent-wax-ink shadow-[inset_0_1px_0_rgba(255,255,255,.1)]">
        S
      </span>
      <span className="font-serif text-[22px] -tracking-[0.01em]">Seven</span>
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[13.5px] font-medium text-ink-primary">{label}</span>
      {children}
    </label>
  );
}

function Input({
  value,
  onChange,
  ...rest
}: {
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  autoComplete?: string;
  required?: boolean;
}): JSX.Element {
  return (
    <input
      {...rest}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-9 w-full rounded border border-rule-divider bg-surface-margin px-3 text-[14.5px] text-ink-primary focus:border-accent-wax focus:bg-surface-slip focus:shadow-[0_0_0_2px_var(--accent-wax-tint)] focus:outline-none"
    />
  );
}
